import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  AIStreamingRequest,
  ErrorEvent,
  MessageEvent,
  SSEEvent,
  StatusEvent,
  ToolCallEvent,
} from '../models/sse-events';
import { ConfigService } from './config/config.service';
import { UtilitiesService } from './utilities.service';

@Injectable({
  providedIn: 'root',
})
export class AIStreamingService {
  private eventSubject$ = new Subject<SSEEvent>();
  private statusEventSubject$ = new Subject<StatusEvent>();
  private messageEventSubject$ = new Subject<MessageEvent>();
  private toolCallEventSubject$ = new Subject<ToolCallEvent>();
  private errorEventSubject$ = new Subject<ErrorEvent>();

  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private decoder = new TextDecoder();
  private buffer = '';
  private lastChat: any = {};
  private maxRetries = 1; // Retry once on token expiration

  constructor(
    private configService: ConfigService,
    private utilityService: UtilitiesService,
    private keycloakService: KeycloakService,
  ) {}

  getTenantId() {
    const isDomainResolver = this.configService.getConfig("isDomainResolver");
    return isDomainResolver
      ? (this.utilityService?.extractRealmFromUrl() as string)
      : (sessionStorage.getItem("selectedRealm") as string) || 'master';
    // return (this.utilityService?.extractRealmFromUrl() as string) || 'master'
  }

  /**
   * Connect to SSE streaming endpoint
   */
  connectToStream(request: AIStreamingRequest): Observable<SSEEvent> {
    return new Observable<SSEEvent>((observer) => {
      this.streamRequest(request, observer);
    }).pipe(
      tap((event: SSEEvent) => this.eventSubject$.next(event)),
      catchError((error) => {
        const errorEvent: ErrorEvent = {
          type: 'error',
          error_code: 'STREAM_ERROR',
          error_message: error.message || 'Unknown error occurred',
          timestamp: new Date().toISOString(),
        };
        this.errorEventSubject$.next(errorEvent);
        return throwError(() => error);
      })
    );
  }

  /**
   * Close the streaming connection
   */
  closeStream(): void {
    if (this.reader) {
      this.reader.cancel().catch((error) => {
        console.error('Error closing stream:', error);
      });
      this.reader = null;
    }
    this.buffer = '';
  }

  /**
   * Get all SSE events
   */
  getEvents$(): Observable<SSEEvent> {
    return this.eventSubject$.asObservable();
  }

  /**
   * Get only status events
   */
  getStatusEvents$(): Observable<StatusEvent> {
    return this.statusEventSubject$.asObservable();
  }

  /**
   * Get only message events
   */
  getMessageEvents$(): Observable<MessageEvent> {
    return this.messageEventSubject$.asObservable();
  }

  /**
   * Get only tool call events
   */
  getToolCallEvents$(): Observable<ToolCallEvent> {
    return this.toolCallEventSubject$.asObservable();
  }

  /**
   * Get only error events
   */
  getErrorEvents$(): Observable<ErrorEvent> {
    return this.errorEventSubject$.asObservable();
  }

  /**
   * Perform the actual streaming request using Fetch API
   */
  private async streamRequest(
    request: AIStreamingRequest,
    observer: any,
    retryCount: number = 0
  ): Promise<void> {
    try {
      const url = this.configService.getConfig('agenticUrl') + '/invoke/stream';
      const formData = this.buildFormData(request);

      const headers: HeadersInit = {
        'X-TenantId': this.getTenantId()
      };

      // Get fresh token from Keycloak
      const token = await this.getValidToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        throw new Error('Unable to obtain valid authentication token');
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      // Handle 401 Unauthorized - Token expired
      if (response.status === 401 && retryCount < this.maxRetries) {
        console.log('Token expired, refreshing and retrying...');
        
        // Force token refresh
        await this.refreshToken();
        
        // Retry the request with new token
        return this.streamRequest(request, observer, retryCount + 1);
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const body = response.body;
      if (!body) {
        throw new Error('No response body');
      }

      this.reader = body.getReader();
      await this.readStream(observer);
      observer.complete();
    } catch (error: any) {
      observer.error(error);
    }
  }

  /**
   * Get a valid token, refresh if necessary
   */
  private async getValidToken(): Promise<string | null> {
    try {
      // Check if token is valid
      const isTokenExpired = this.keycloakService.isTokenExpired(5); // Check with 5 second buffer
      
      if (isTokenExpired) {
        console.log('Token expired or about to expire, refreshing...');
        await this.refreshToken();
      }
      
      const token = await this.keycloakService.getToken();
      return token;
    } catch (error) {
      console.error('Error getting valid token:', error);
      return null;
    }
  }

  /**
   * Force refresh the Keycloak token
   */
  private async refreshToken(): Promise<void> {
    try {
      // Use Keycloak's updateToken method to refresh
      const refreshed = await this.keycloakService.updateToken(-1); // Force refresh
      
      if (refreshed) {
        console.log('Token refreshed successfully');
      } else {
        console.log('Token still valid, no refresh needed');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      // If refresh fails, try to login again
      try {
        await this.keycloakService.login();
      } catch (loginError) {
        console.error('Login failed after token refresh error:', loginError);
        throw new Error('Authentication failed. Please log in again.');
      }
    }
  }

  /**
   * Read the streaming response
   */
  private async readStream(observer: any): Promise<void> {
    if (!this.reader) {
      return;
    }

    try {
      while (true) {
        const { done, value } = await this.reader.read();

        if (done) {
          if (this.buffer.trim()) {
            this.processLine(this.buffer, observer);
            this.buffer = '';
          }
          break;
        }

        const text = this.decoder.decode(value, { stream: true });
        this.buffer += text;

        const lines = this.buffer.split('\n');
        for (let i = 0; i < lines.length - 1; i++) {
          this.processLine(lines[i], observer);
        }
        this.buffer = lines[lines.length - 1];
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        observer.error(error);
      }
    }
  }

  /**
   * Process a single line from the stream
   */
  private processLine(line: string, observer: any): void {
    const trimmedLine = line.trim();
    if (!trimmedLine) {
      return;
    }

    if (trimmedLine.startsWith('event:')) {
      return;
    }

    if (trimmedLine.startsWith('data:')) {
      const dataString = trimmedLine.substring(5).trim();
      try {
        const event = JSON.parse(dataString) as SSEEvent;
        this.handleEvent(event, observer);
      } catch (error) {
        console.error('Failed to parse event data:', error, dataString);
      }
    }
  }

  /**
   * Handle parsed SSE event and distribute to appropriate subjects
   */
  private handleEvent(event: SSEEvent, observer: any): void {
    observer.next(event);

    switch (event.type) {
      case 'status':
        this.statusEventSubject$.next(event as StatusEvent);
        break;
      case 'message':
        this.messageEventSubject$.next(event as MessageEvent);
        break;
      case 'tool_call':
        this.statusEventSubject$.next(event as StatusEvent);
        break;
      case 'error':
        this.errorEventSubject$.next(event as ErrorEvent);
        break;
    }
  }

  /**
   * Build FormData from request object
   */
  private buildFormData(request: AIStreamingRequest): FormData {
    const formData = new FormData();
    formData.append('query', request.query);
    formData.append('thread_id', request.thread_id);
    formData.append('project_id', request.project_id);
    if (request.files) {
      formData.append('files', request.files);
    }
    if (request.service_secrets) {
      formData.append('service_secrets', request.service_secrets);
    }
    return formData;
  }

  setLastChat(lastChat: any) {
    this.lastChat = lastChat;
  }

  getLastChat() {
    return this.lastChat;
  }

  clearLastChat() {
    this.lastChat = {};
  }
  
}
